import { useState, useEffect, useCallback, FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client';
import { ContestSettings } from './ContestSettings';
import './Settings.css';

interface CloudflareAccount {
  id: number;
  name: string;
  account_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface FormState {
  name: string;
  account_id: string;
  api_token: string;
  is_active: boolean;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

const XIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const PlusIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function Settings(): JSX.Element {
  const [accounts, setAccounts] = useState<CloudflareAccount[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CloudflareAccount | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CloudflareAccount | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const list = await api<CloudflareAccount[]>('/api/settings/cloudflare-accounts');
      setAccounts(list);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load accounts');
    }
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleTest = async (account: CloudflareAccount): Promise<void> => {
    setTestingId(account.id);
    try {
      const result = await api<{ valid: boolean; message: string }>(
        `/api/settings/cloudflare-accounts/${account.id}/test`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (result.valid) {
        showToast('success', `${account.name}: ${result.message}`);
      } else {
        showToast('error', `${account.name}: ${result.message}`);
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api(`/api/settings/cloudflare-accounts/${deleteTarget.id}`, { method: 'DELETE' });
      showToast('success', `${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      await refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleFormSuccess = async (action: 'created' | 'updated', name: string): Promise<void> => {
    setShowForm(false);
    setEditing(null);
    showToast('success', `${name} ${action}`);
    await refresh();
  };

  return (
    <div className="set-root">
      <div className="set-scroll">
        <header className="set-header">
          <h1 className="set-title">Settings</h1>
          <p className="set-subtitle">
            Configure connected accounts and credentials used by automation features.
          </p>
        </header>

        <section className="set-card">
          <div className="set-card-header">
            <div>
              <h2 className="set-card-title">Cloudflare Accounts</h2>
              <p className="set-card-subtitle">DNS management tokens, scoped per CF account.</p>
            </div>
            <button
              type="button"
              className="set-btn set-btn--primary"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
            >
              <PlusIcon />
              Add account
            </button>
          </div>

          {accounts === null && <div className="set-empty"><span>Loading…</span></div>}

          {accounts !== null && accounts.length === 0 && (
            <div className="set-empty">
              <div className="set-empty-icon">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
                </svg>
              </div>
              <h3 className="set-empty-title">No Cloudflare accounts</h3>
              <p className="set-empty-text">Connect your first Cloudflare account to manage DNS.</p>
            </div>
          )}

          {accounts !== null && accounts.length > 0 && (
            <table className="set-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Account ID</th>
                  <th>Status</th>
                  <th className="set-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="set-cell-id">
                        <div className="set-avatar">CF</div>
                        <span className="set-name">{a.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="set-mono">{a.account_id}</span>
                    </td>
                    <td>
                      <span className={`set-badge ${a.is_active ? 'set-badge--active' : 'set-badge--inactive'}`}>
                        <span className="set-dot" />
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="set-col-actions">
                      <div className="set-row-actions">
                        <button
                          type="button"
                          className="set-btn set-btn--secondary set-btn--sm"
                          onClick={() => void handleTest(a)}
                          disabled={testingId === a.id}
                        >
                          {testingId === a.id ? 'Testing…' : 'Test'}
                        </button>
                        <button
                          type="button"
                          className="set-btn set-btn--secondary set-btn--sm"
                          onClick={() => {
                            setEditing(a);
                            setShowForm(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="set-btn set-btn--danger set-btn--sm"
                          onClick={() => setDeleteTarget(a)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <SpaceshipSection showToast={showToast} />

        <ContestSettings showToast={showToast} />

        <TeamPasswordSection
          showToast={showToast}
          title="Team Password"
          subtitle={
            <>
              Single shared password for the team-only license-run page at <code>/team</code>.
              Rotating signs everyone out — they'll need the new one to come back in.
            </>
          }
          apiPath="/api/settings/team-password"
          notSetHint={<>Set a password to enable the <code>/team</code> page.</>}
          rotatedNotice="Team password updated. All open team sessions have been signed out."
        />

        <TeamPasswordSection
          showToast={showToast}
          title="Team Lead Password"
          subtitle={
            <>
              Single shared password for the team-lead page at <code>/teamlead</code> (Inventory
              Pull + Licensing). Rotating signs out every active team-lead session.
            </>
          }
          apiPath="/api/settings/team-lead-password"
          notSetHint={<>Set a password to enable the <code>/teamlead</code> page.</>}
          rotatedNotice="Team lead password updated. All open team-lead sessions have been signed out."
        />

        <SerkenMigrationSection showToast={showToast} />
      </div>

      {showForm && (
        <CloudflareAccountForm
          account={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSuccess={handleFormSuccess}
          showToastError={(msg) => showToast('error', msg)}
        />
      )}

      {deleteTarget && (
        <div className="set-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="set-modal" onClick={(e) => e.stopPropagation()}>
            <div className="set-modal-header">
              <h3 className="set-modal-title">Delete this Cloudflare account?</h3>
              <button type="button" className="set-icon-btn" onClick={() => setDeleteTarget(null)}>
                <XIcon />
              </button>
            </div>
            <div className="set-modal-body">
              <p>
                This removes the stored API token for <strong>{deleteTarget.name}</strong>. Any
                automation that depends on this account will fail until it's re-added.
              </p>
            </div>
            <div className="set-modal-footer">
              <button
                type="button"
                className="set-btn set-btn--ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="set-btn set-btn--danger"
                onClick={() => void handleDelete()}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`set-toast set-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── Add/Edit form (modal) ─────────────────────────────── */

function CloudflareAccountForm({
  account,
  onClose,
  onSuccess,
  showToastError,
}: {
  account: CloudflareAccount | null;
  onClose: () => void;
  onSuccess: (action: 'created' | 'updated', name: string) => void;
  showToastError: (msg: string) => void;
}): JSX.Element {
  const [form, setForm] = useState<FormState>({
    name: account?.name ?? '',
    account_id: account?.account_id ?? '',
    api_token: '',
    is_active: account?.is_active ?? false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    try {
      if (account) {
        const patch: Partial<FormState> = {
          name: form.name,
          account_id: form.account_id,
          is_active: form.is_active,
        };
        if (form.api_token.length > 0) patch.api_token = form.api_token;
        await api(`/api/settings/cloudflare-accounts/${account.id}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
        onSuccess('updated', form.name);
      } else {
        await api('/api/settings/cloudflare-accounts', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        onSuccess('created', form.name);
      }
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToastError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="set-modal-overlay" onClick={onClose}>
      <div className="set-modal" onClick={(e) => e.stopPropagation()}>
        <div className="set-modal-header">
          <h3 className="set-modal-title">
            {account ? 'Edit Cloudflare account' : 'Add Cloudflare account'}
          </h3>
          <button type="button" className="set-icon-btn" onClick={onClose}>
            <XIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="set-modal-body">
            <div className="set-field">
              <label className="set-label" htmlFor="cf-account-status">Status</label>
              <select
                id="cf-account-status"
                className="set-input"
                value={String(form.is_active)}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === 'true' })}
                aria-describedby="cf-account-status-hint"
              >
                <option value="false">Inactive — customer isolated</option>
                <option value="true">Active — shared automation</option>
              </select>
              <p className="set-hint" id="cf-account-status-hint">
                Inactive accounts stay out of the shared DNS automation pool. They can still be
                assigned to customer builds.
              </p>
            </div>
            <div className="set-field">
              <label className="set-label">Display name</label>
              <input
                className="set-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="cloudflare@example.com"
                required
                autoFocus
              />
              <p className="set-hint">A free-form label — typically the email of the CF account.</p>
            </div>
            <div className="set-field">
              <label className="set-label">Account ID</label>
              <input
                className="set-input set-input-mono"
                value={form.account_id}
                onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                placeholder="32 hex chars"
                pattern="[a-f0-9]{32}"
                required={!account}
              />
              <p className="set-hint">From your Cloudflare dashboard → right sidebar → Account ID.</p>
            </div>
            <div className="set-field">
              <label className="set-label">
                API token{account && <span className="set-label-suffix"> (leave blank to keep current)</span>}
              </label>
              <input
                className="set-input set-input-mono"
                type="text"
                value={form.api_token}
                onChange={(e) => setForm({ ...form, api_token: e.target.value })}
                placeholder={account ? '••••••••••••••••' : 'Paste new token'}
                required={!account}
                autoComplete="off"
              />
              <p className="set-hint">
                Stored encrypted (AES-256-GCM). The plaintext value never leaves the server.
              </p>
            </div>
          </div>
          <div className="set-modal-footer">
            <button
              type="button"
              className="set-btn set-btn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="set-btn set-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : account ? 'Save changes' : 'Add account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Spaceship section (singleton) ─────────────────────── */

interface SpaceshipAccountState {
  has_api_key: boolean;
  has_api_secret: boolean;
  contact_id: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_address1: string | null;
  contact_address2: string | null;
  contact_city: string | null;
  contact_state_province: string | null;
  contact_postal_code: string | null;
  contact_country: string | null;
  contact_organization: string | null;
  contact_phone: string | null;
  updated_at: string;
}

const COUNTRY_PLACEHOLDER = 'US';

function SpaceshipSection({
  showToast,
}: {
  showToast: (type: 'success' | 'error', message: string) => void;
}): JSX.Element {
  const [acc, setAcc] = useState<SpaceshipAccountState | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [draft, setDraft] = useState({
    api_key: '',
    api_secret: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_address1: '',
    contact_address2: '',
    contact_city: '',
    contact_state_province: '',
    contact_postal_code: '',
    contact_country: '',
    contact_organization: '',
    contact_phone: '',
  });

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await api<SpaceshipAccountState>('/api/settings/spaceship-account');
      setAcc(data);
      setDraft({
        api_key: '',
        api_secret: '',
        contact_first_name: data.contact_first_name ?? '',
        contact_last_name: data.contact_last_name ?? '',
        contact_email: data.contact_email ?? '',
        contact_address1: data.contact_address1 ?? '',
        contact_address2: data.contact_address2 ?? '',
        contact_city: data.contact_city ?? '',
        contact_state_province: data.contact_state_province ?? '',
        contact_postal_code: data.contact_postal_code ?? '',
        contact_country: data.contact_country ?? '',
        contact_organization: data.contact_organization ?? '',
        contact_phone: data.contact_phone ?? '',
      });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load Spaceship config');
    }
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};
      if (draft.api_key) patch.api_key = draft.api_key;
      if (draft.api_secret) patch.api_secret = draft.api_secret;
      patch.contact_first_name = draft.contact_first_name || null;
      patch.contact_last_name = draft.contact_last_name || null;
      patch.contact_email = draft.contact_email || null;
      patch.contact_address1 = draft.contact_address1 || null;
      patch.contact_address2 = draft.contact_address2 || null;
      patch.contact_city = draft.contact_city || null;
      patch.contact_state_province = draft.contact_state_province || null;
      patch.contact_postal_code = draft.contact_postal_code || null;
      patch.contact_country = draft.contact_country.toUpperCase() || null;
      patch.contact_organization = draft.contact_organization || null;
      patch.contact_phone = draft.contact_phone || null;

      await api('/api/settings/spaceship-account', {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      showToast('success', 'Spaceship configuration saved');
      setEditing(false);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  const test = async (): Promise<void> => {
    setTesting(true);
    try {
      const result = await api<{ valid: boolean; message: string }>(
        '/api/settings/spaceship-account/test',
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (result.valid) showToast('success', `Spaceship: ${result.message}`);
      else showToast('error', `Spaceship: ${result.message}`);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  if (acc === null) {
    return (
      <section className="set-card">
        <div className="set-empty"><span>Loading…</span></div>
      </section>
    );
  }

  const credsConfigured = acc.has_api_key && acc.has_api_secret;

  return (
    <section className="set-card">
      <div className="set-card-header">
        <div>
          <h2 className="set-card-title">Spaceship</h2>
          <p className="set-card-subtitle">
            Domain registrar for availability checks + purchase. Single account.
          </p>
        </div>
        <div className="set-row-actions">
          {!editing && (
            <>
              <button
                type="button"
                className="set-btn set-btn--secondary set-btn--sm"
                onClick={() => void test()}
                disabled={testing || !credsConfigured}
              >
                {testing ? 'Testing…' : 'Test'}
              </button>
              <button
                type="button"
                className="set-btn set-btn--primary set-btn--sm"
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                type="button"
                className="set-btn set-btn--ghost set-btn--sm"
                onClick={() => {
                  setEditing(false);
                  void refresh();
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="set-btn set-btn--primary set-btn--sm"
                onClick={() => void save()}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="set-grid">
        <SpaceshipField label="API key" editing={editing} masked={acc.has_api_key}
          value={draft.api_key}
          onChange={(v) => setDraft({ ...draft, api_key: v })}
          placeholder={acc.has_api_key ? 'Saved — leave blank to keep' : 'Paste API key'}
          mono
        />
        <SpaceshipField label="API secret" editing={editing} masked={acc.has_api_secret}
          value={draft.api_secret}
          onChange={(v) => setDraft({ ...draft, api_secret: v })}
          placeholder={acc.has_api_secret ? 'Saved — leave blank to keep' : 'Paste API secret'}
          mono
        />
        <SpaceshipField label="Contact ID" editing={false}
          value={acc.contact_id ?? ''}
          placeholder={acc.contact_id ?? '— created on first purchase'}
          onChange={() => undefined}
          mono
          readonly
        />
        <SpaceshipField label="First name" editing={editing}
          value={draft.contact_first_name} onChange={(v) => setDraft({ ...draft, contact_first_name: v })}
        />
        <SpaceshipField label="Last name" editing={editing}
          value={draft.contact_last_name} onChange={(v) => setDraft({ ...draft, contact_last_name: v })}
        />
        <SpaceshipField label="Email" editing={editing}
          value={draft.contact_email} onChange={(v) => setDraft({ ...draft, contact_email: v })}
          placeholder="contact@example.com"
        />
        <SpaceshipField label="Organization" editing={editing}
          value={draft.contact_organization} onChange={(v) => setDraft({ ...draft, contact_organization: v })}
        />
        <SpaceshipField label="Phone" editing={editing}
          value={draft.contact_phone} onChange={(v) => setDraft({ ...draft, contact_phone: v })}
          placeholder="+1.5551234567"
        />
        <SpaceshipField label="Address line 1" editing={editing} colSpan={2}
          value={draft.contact_address1} onChange={(v) => setDraft({ ...draft, contact_address1: v })}
        />
        <SpaceshipField label="Address line 2" editing={editing} colSpan={2}
          value={draft.contact_address2} onChange={(v) => setDraft({ ...draft, contact_address2: v })}
        />
        <SpaceshipField label="City" editing={editing}
          value={draft.contact_city} onChange={(v) => setDraft({ ...draft, contact_city: v })}
        />
        <SpaceshipField label="State / Province" editing={editing}
          value={draft.contact_state_province} onChange={(v) => setDraft({ ...draft, contact_state_province: v })}
        />
        <SpaceshipField label="Postal code" editing={editing}
          value={draft.contact_postal_code} onChange={(v) => setDraft({ ...draft, contact_postal_code: v })}
        />
        <SpaceshipField label="Country (2-letter)" editing={editing}
          value={draft.contact_country} onChange={(v) => setDraft({ ...draft, contact_country: v.toUpperCase() })}
          placeholder={COUNTRY_PLACEHOLDER}
          mono
        />
      </div>
    </section>
  );
}

/* ─── Team password section ─────────────────────────────── */

interface TeamPasswordStatus {
  isSet: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function generateStrongPassword(): string {
  const bytes = new Uint8Array(18); // 18 bytes → 24 base64url chars
  crypto.getRandomValues(bytes);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

type TeamPwMode = 'view' | 'type-own' | 'reveal';

function TeamPasswordSection({
  showToast,
  title,
  subtitle,
  apiPath,
  notSetHint,
  rotatedNotice,
}: {
  showToast: (type: 'success' | 'error', message: string) => void;
  title: string;
  subtitle: ReactNode;
  apiPath: string;
  notSetHint: ReactNode;
  rotatedNotice: string;
}): JSX.Element {
  const [status, setStatus] = useState<TeamPasswordStatus | null>(null);
  const [mode, setMode] = useState<TeamPwMode>('view');
  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await api<TeamPasswordStatus>(apiPath);
      setStatus(data);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load password status');
    }
  }, [showToast, apiPath]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const submitTypedPassword = async (): Promise<void> => {
    if (pw.length < 12) {
      showToast('error', 'Password must be at least 12 characters.');
      return;
    }
    if (pw !== pwConfirm) {
      showToast('error', 'Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await api(apiPath, {
        method: 'PUT',
        body: JSON.stringify({ password: pw }),
      });
      showToast('success', rotatedNotice);
      setPw('');
      setPwConfirm('');
      setMode('view');
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  const generateAndSave = async (): Promise<void> => {
    setGenerating(true);
    try {
      const generated = generateStrongPassword();
      await api(apiPath, {
        method: 'PUT',
        body: JSON.stringify({ password: generated }),
      });
      setRevealed(generated);
      setMode('reveal');
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToast('error', msg);
    } finally {
      setGenerating(false);
    }
  };

  const copyRevealed = async (): Promise<void> => {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(revealed);
      showToast('success', 'Copied to clipboard');
    } catch {
      showToast('error', 'Copy failed — select and copy manually.');
    }
  };

  const dismissReveal = (): void => {
    setRevealed(null);
    setMode('view');
  };

  if (status === null) {
    return (
      <section className="set-card">
        <div className="set-empty"><span>Loading…</span></div>
      </section>
    );
  }

  return (
    <section className="set-card">
      <div className="set-card-header">
        <div>
          <h2 className="set-card-title">{title}</h2>
          <p className="set-card-subtitle">{subtitle}</p>
        </div>
      </div>

      {mode === 'view' && (
        <div className="set-team-pw-body">
          <div className="set-team-pw-status">
            {status.isSet ? (
              <>
                <span className="set-badge set-badge--active">
                  <span className="set-dot" />
                  Set
                </span>
                <span className="set-team-pw-meta">
                  Last rotated {relativeTime(status.updatedAt)}
                  {status.updatedBy ? <> by <strong>{status.updatedBy}</strong></> : null}
                </span>
              </>
            ) : (
              <>
                <span className="set-badge set-badge--inactive">
                  <span className="set-dot" />
                  Not set
                </span>
                <span className="set-team-pw-meta">{notSetHint}</span>
              </>
            )}
          </div>
          <div className="set-team-pw-actions">
            <button
              type="button"
              className="set-btn set-btn--secondary"
              onClick={() => setMode('type-own')}
            >
              Type my own
            </button>
            <button
              type="button"
              className="set-btn set-btn--primary"
              onClick={() => void generateAndSave()}
              disabled={generating}
            >
              {generating ? 'Generating…' : 'Generate strong'}
            </button>
          </div>
        </div>
      )}

      {mode === 'type-own' && (
        <div className="set-team-pw-body">
          <div className="set-field">
            <label className="set-label">New password (12+ chars)</label>
            <input
              className="set-input set-input-mono"
              type="text"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              autoComplete="new-password"
              placeholder="Min 12 characters"
            />
          </div>
          <div className="set-field">
            <label className="set-label">Confirm</label>
            <input
              className="set-input set-input-mono"
              type="text"
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Re-type the password"
            />
          </div>
          <div className="set-team-pw-actions">
            <button
              type="button"
              className="set-btn set-btn--ghost"
              onClick={() => {
                setMode('view');
                setPw('');
                setPwConfirm('');
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="set-btn set-btn--primary"
              onClick={() => void submitTypedPassword()}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save password'}
            </button>
          </div>
        </div>
      )}

      {mode === 'reveal' && revealed && (
        <div className="set-team-pw-body set-team-pw-reveal">
          <div className="set-team-pw-warn">
            New password generated. <strong>Save it now</strong> — once you close this view it can't be recovered.
            All open team sessions have been signed out.
          </div>
          <div className="set-team-pw-revealbox">
            <code className="set-team-pw-cleartext">{revealed}</code>
            <button
              type="button"
              className="set-btn set-btn--secondary set-btn--sm"
              onClick={() => void copyRevealed()}
            >
              Copy
            </button>
          </div>
          <div className="set-team-pw-actions">
            <button
              type="button"
              className="set-btn set-btn--primary"
              onClick={dismissReveal}
            >
              Done — I've saved it
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ─── Serken migration section (singleton) ──────────────── */

interface MigrationConfigState {
  ops_mailbox: string | null;
  updated_at: string;
  updated_by: string | null;
}

function SerkenMigrationSection({
  showToast,
}: {
  showToast: (type: 'success' | 'error', message: string) => void;
}): JSX.Element {
  const [config, setConfig] = useState<MigrationConfigState | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await api<MigrationConfigState>('/api/settings/migration-config');
      setConfig(data);
      setDraft(data.ops_mailbox ?? '');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load migration config');
    }
  }, [showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await api('/api/settings/migration-config', {
        method: 'PUT',
        body: JSON.stringify({ ops_mailbox: draft.trim() }),
      });
      showToast('success', draft.trim() ? 'Ops mailbox saved' : 'Ops mailbox cleared');
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Save failed';
      showToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  if (config === null) {
    return (
      <section className="set-card">
        <div className="set-empty"><span>Loading…</span></div>
      </section>
    );
  }

  const dirty = draft.trim() !== (config.ops_mailbox ?? '');

  return (
    <section className="set-card">
      <div className="set-card-header">
        <div>
          <h2 className="set-card-title">Serken Migration</h2>
          <p className="set-card-subtitle">
            Operations mailbox used by the renewal migration. Applies to every destination panel.
          </p>
        </div>
      </div>

      <div className="set-team-pw-body">
        <div className="set-field">
          <label className="set-label">Ops mailbox</label>
          <input
            className="set-input set-input-mono"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="ops@example.com"
            autoComplete="off"
          />
          <p className="set-hint">
            Mail sent to each destination panel's <code>admin@</code> is routed here. A destination{' '}
            <code>admin@</code> is a Cloud Identity account with no Workspace license, so it has no
            mailbox of its own - without this, the data migration's confirmation and verification
            emails would be lost. The original <code>To:</code> header is preserved, so each message
            can still be traced back to its destination panel. Leave blank to clear.
          </p>
        </div>

        <div className="set-team-pw-status">
          {config.ops_mailbox ? (
            <>
              <span className="set-badge set-badge--active">
                <span className="set-dot" />
                Set
              </span>
              <span className="set-team-pw-meta">
                Last updated {relativeTime(config.updated_at)}
                {config.updated_by ? <> by <strong>{config.updated_by}</strong></> : null}
              </span>
            </>
          ) : (
            <>
              <span className="set-badge set-badge--inactive">
                <span className="set-dot" />
                Not set
              </span>
              <span className="set-team-pw-meta">
                Data migration confirmation mail to destination <code>admin@</code> addresses has
                nowhere to land until this is configured.
              </span>
            </>
          )}
        </div>

        <div className="set-team-pw-actions">
          <button
            type="button"
            className="set-btn set-btn--primary"
            onClick={() => void save()}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </section>
  );
}

function SpaceshipField({
  label,
  editing,
  value,
  onChange,
  placeholder,
  masked = false,
  mono = false,
  readonly = false,
  colSpan,
}: {
  label: string;
  editing: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  masked?: boolean;
  mono?: boolean;
  readonly?: boolean;
  colSpan?: 2;
}): JSX.Element {
  return (
    <div className={`set-field ${colSpan === 2 ? 'set-field--wide' : ''}`}>
      <label className="set-label">{label}</label>
      {editing && !readonly ? (
        <input
          className={`set-input ${mono ? 'set-input-mono' : ''}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
      ) : (
        <div className={`set-readout ${mono ? 'set-readout--mono' : ''} ${!value ? 'set-readout--empty' : ''}`}>
          {masked && !value ? '••••••••' : value || (placeholder ?? '—')}
        </div>
      )}
    </div>
  );
}
